import Pusher, { ChannelAuthorizationOptions } from 'pusher-js'
import { logger } from 'shared/services/logger'

export interface PusherSocketConfig {
  socketName: string
  appKey: string | undefined
  wsHost: string | undefined
  wsPort?: number
  channelAuthorization?: ChannelAuthorizationOptions
}

export type SocketEventHandler = (eventName: string, data: unknown) => void

export interface PusherSocket {
  subscribe: (channelName: string, onEvent: SocketEventHandler) => () => void
}

export const createPusherSocket = (
  config: PusherSocketConfig
): PusherSocket => {
  let pusher: Pusher | null = null

  const getPusher = (): Pusher | null => {
    if (!config.appKey || !config.wsHost) {
      logger.warn('socket.configuration_missing', {
        socketName: config.socketName,
        hasAppKey: !!config.appKey,
        hasWsHost: !!config.wsHost
      })
      return null
    }

    if (pusher) {
      return pusher
    }

    pusher = new Pusher(config.appKey, {
      cluster: 'mt1',
      wsHost: config.wsHost,
      wssPort: config.wsPort ?? 443,
      forceTLS: true,
      channelAuthorization: config.channelAuthorization
    })

    logger.track('socket.connection_initialized', {
      socketName: config.socketName,
      host: config.wsHost,
      port: config.wsPort ?? 443,
      usesChannelAuthorization: !!config.channelAuthorization
    })

    pusher.connection.bind(
      'state_change',
      (states: { previous: string; current: string }) => {
        logger.track('socket.state_changed', {
          socketName: config.socketName,
          host: config.wsHost,
          port: config.wsPort ?? 443,
          activeChannelCount: pusher?.allChannels().length ?? 0,
          previous: states.previous,
          current: states.current
        })
      }
    )
    pusher.connection.bind('error', (error: unknown) => {
      logger.exception(
        'socket.connection_failed',
        error,
        {
          socketName: config.socketName,
          host: config.wsHost,
          port: config.wsPort ?? 443
        },
        { category: 'socket' }
      )
    })

    return pusher
  }

  const subscribe = (
    channelName: string,
    onEvent: SocketEventHandler
  ): (() => void) => {
    const instance = getPusher()
    if (!instance) {
      return () => {}
    }

    const channel = instance.subscribe(channelName)

    const handleEvent = (eventName: string, data: unknown) => {
      if (eventName.startsWith('pusher')) return
      onEvent(eventName, data)
    }
    channel.bind_global(handleEvent)

    channel.bind('pusher:subscription_error', (status: unknown) => {
      logger.error('socket.subscription_failed', {
        socketName: config.socketName,
        host: config.wsHost,
        channelName,
        status
      })
    })

    return () => {
      channel.unbind_global(handleEvent)
      instance.unsubscribe(channelName)

      if (instance.allChannels().length === 0) {
        instance.disconnect()
        pusher = null
      }
    }
  }

  return { subscribe }
}
